// Persisted operator preferences for live call audio boost.
//
// Two keys, deliberately separate:
//   - boost.enabled — master switch (default off so a fresh operator
//     hears nothing unusual; they have to opt in explicitly).
//   - boost.gain    — slider position (1.0..3.0). Persists across the
//     enabled toggle so flipping off/on doesn't reset the user's
//     last position; they only configure it once.
//
// Min gain is 1.0 (not 0): the switch is the OFF affordance; the
// slider only ever boosts. Going below 1.0 would attenuate, which
// the UI doesn't expose.

import { safeRead, safeWrite } from './safeStorage'

export const CALL_BOOST_ENABLED_KEY = 'pixellab.call.boost.enabled'
export const CALL_BOOST_GAIN_KEY = 'pixellab.call.boost.gain'

export const CALL_BOOST_GAIN_MIN = 1
export const CALL_BOOST_GAIN_MAX = 3
export const CALL_BOOST_GAIN_DEFAULT = 1.5
export const CALL_BOOST_ENABLED_DEFAULT = false

export function getCallBoostEnabled(): boolean {
  const raw = safeRead(CALL_BOOST_ENABLED_KEY)
  if (raw === null) return CALL_BOOST_ENABLED_DEFAULT
  return raw === 'true'
}

export function setCallBoostEnabled(v: boolean): void {
  safeWrite(CALL_BOOST_ENABLED_KEY, v ? 'true' : 'false')
}

export function getCallBoostGain(): number {
  const raw = safeRead(CALL_BOOST_GAIN_KEY)
  if (!raw) return CALL_BOOST_GAIN_DEFAULT
  const n = Number(raw)
  if (!isFinite(n)) return CALL_BOOST_GAIN_DEFAULT
  if (n < CALL_BOOST_GAIN_MIN) return CALL_BOOST_GAIN_MIN
  if (n > CALL_BOOST_GAIN_MAX) return CALL_BOOST_GAIN_MAX
  return n
}

export function setCallBoostGain(v: number): void {
  if (!isFinite(v)) return
  const clamped = Math.max(CALL_BOOST_GAIN_MIN, Math.min(CALL_BOOST_GAIN_MAX, v))
  safeWrite(CALL_BOOST_GAIN_KEY, String(clamped))
}
